/**
 * The client's consent to a hand-off between sedi — «Evasione tra sedi», spec §4 / §6.2.
 *
 * Lives on the SENDING sede's domain: the backend builds links as
 * `https://<sede-domain>/consenso/<token>` (landing.services.passaggi.consenso_url).
 * Same contract as ConfirmSlotPage: `/api/landing/passaggi/consenso/<token>/` is same-origin
 * for the browser and the sede's nginx reverse-proxies it to avmg.it (nginx.conf.example),
 * so no CORS and no cookies (`credentials: 'omit'`). The payload carries no client data
 * beyond the comune. Copy: ./strings.ts.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { SedeConfig } from '../../sedi'
import { useNoindexHead } from '../../reviews/ReviewChrome'
import {
  Azioni,
  Collegamento,
  Contatti,
  Corpo,
  Elenco,
  Esito,
  Fatti,
  Nota,
  PaginaPubblica,
  Sezione,
  Testo,
  Titolo,
  type Mittente,
} from '../pubblica/PaginaPubblica'
import { CONSENSO, dataLunga, riempi } from './strings'

/** `GET/POST /api/landing/passaggi/consenso/<token>/` (spec §3.7). */
interface Payload {
  stato: 'in_attesa' | 'dato' | 'negato' | 'scaduto' | 'annullata'
  scade_il: string | null
  comune: string
  testo_versione: string
  mittente: Mittente
  destinataria: { denominazione: string; piva: string; indirizzo: string; telefono: string; email: string; privacy_url: string }
}

type Stato = { kind: 'loading' } | { kind: 'not_found' } | { kind: 'error' } | { kind: 'ok'; d: Payload }

/** The body is a payload, or the page shows the error state (never a half-rendered form). */
function comePayload(j: unknown): Payload | null {
  const o = j as Partial<Payload> | null
  return o && typeof o.stato === 'string' && o.mittente && o.destinataria ? (o as Payload) : null
}

async function leggi(r: Response): Promise<Stato> {
  const d = comePayload(await r.json().catch(() => null))
  return d ? { kind: 'ok', d } : { kind: 'error' }
}

export default function ConsensoPage({ token, sede }: { token: string; sede: SedeConfig }) {
  const [stato, setStato] = useState<Stato>({ kind: 'loading' })
  const [invio, setInvio] = useState(false)
  // Fresh WhatsApp consent for the receiving sede — unticked; the one given to {A} is never inherited.
  const [whatsapp, setWhatsapp] = useState(false)
  const url = `/api/landing/passaggi/consenso/${token}/`
  // Header and footer before the payload lands (and on 404 / error): this site's own sede.
  const fallback: Mittente = {
    nome: sede.brand.name,
    citta: sede.legalPages?.city ?? sede.contact.address,
    dominio: sede.domains[0] ?? '',
    telefono: sede.contact.phone,
    email: sede.contact.email,
  }
  const mittente = stato.kind === 'ok' ? stato.d.mittente : fallback
  useNoindexHead(riempi(CONSENSO.documentTitle, { A: mittente.nome }))

  useEffect(() => {
    let cancelled = false
    fetch(url, { credentials: 'omit' })
      .then(async (r) => {
        // 410: the link expired just now — the body is still the payload (stato «scaduto»).
        const next: Stato = r.status === 404 ? { kind: 'not_found' } : r.ok || r.status === 410 ? await leggi(r) : { kind: 'error' }
        if (!cancelled) setStato(next)
      })
      .catch(() => {
        if (!cancelled) setStato({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [url])

  const rispondi = async (esito: 'acconsento' | 'non_acconsento') => {
    setInvio(true)
    try {
      const r = await fetch(url, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ esito, whatsapp_consent: esito === 'acconsento' && whatsapp }),
      })
      if (r.status === 404) setStato({ kind: 'not_found' })
      else if (r.ok) setStato(await leggi(r))
      else if (r.status === 409 || r.status === 410) {
        // Already answered / expired meanwhile: the error body is not the payload — re-read the page state.
        const again = await fetch(url, { credentials: 'omit' })
        setStato(again.ok || again.status === 410 ? await leggi(again) : { kind: 'error' })
      } else setStato({ kind: 'error' })
    } catch {
      setStato({ kind: 'error' })
    } finally {
      setInvio(false)
    }
  }

  const pagina = (corpo: ReactNode) => (
    <PaginaPubblica mittente={mittente} tipo={CONSENSO.tipo}>
      {corpo}
    </PaginaPubblica>
  )
  if (stato.kind === 'loading') return pagina(<Esito testo={CONSENSO.caricamento} />)
  if (stato.kind === 'not_found') return pagina(<Esito testo={CONSENSO.nonTrovato} />)
  if (stato.kind === 'error') return pagina(<Esito testo={CONSENSO.errore} />)

  const d = stato.d
  const v = { A: d.mittente.nome, B: d.destinataria.denominazione, comune: d.comune, data: d.scade_il ? dataLunga(d.scade_il) : '' }
  const contattiA = { telefono: d.mittente.telefono, email: d.mittente.email }
  if (d.stato === 'annullata') return pagina(<Esito testo={CONSENSO.annullata} />)
  if (d.stato === 'dato') {
    return pagina(<Esito testo={riempi(CONSENSO.dato, v)} contatti={{ telefono: d.destinataria.telefono, email: d.destinataria.email }} />)
  }
  // «…contatti {A}»: the sentence asks the client to reach the sending sede, so its contacts follow it.
  if (d.stato === 'negato') return pagina(<Esito testo={riempi(CONSENSO.negato, v)} contatti={contattiA} />)
  if (d.stato === 'scaduto') return pagina(<Esito testo={riempi(CONSENSO.scaduto, v)} contatti={contattiA} />)

  return pagina(
    <Corpo>
      <Titolo titolo={riempi(CONSENSO.titolo, v)} lead={riempi(CONSENSO.lead, v)} />
      <Sezione titolo={riempi(CONSENSO.cosaRicevera, v)}>
        <Elenco voci={[...CONSENSO.cosaRiceveraVoci]} />
      </Sezione>
      <Sezione titolo={riempi(CONSENSO.chiE, v)}>
        <Fatti
          righe={[
            { label: CONSENSO.chiEVoci.denominazione, valore: d.destinataria.denominazione },
            { label: CONSENSO.chiEVoci.piva, valore: d.destinataria.piva },
            { label: CONSENSO.chiEVoci.indirizzo, valore: d.destinataria.indirizzo },
            { label: CONSENSO.chiEVoci.telefono, valore: d.destinataria.telefono },
            { label: CONSENSO.chiEVoci.email, valore: d.destinataria.email },
          ].filter((r) => r.valore)}
        />
        {d.destinataria.privacy_url && (
          <div className="mt-3">
            <Collegamento href={d.destinataria.privacy_url}>{riempi(CONSENSO.informativa, v)}</Collegamento>
          </div>
        )}
      </Sezione>
      <Sezione titolo={CONSENSO.cosaCambia}>
        <Testo>{riempi(CONSENSO.cosaCambiaTesto, v)}</Testo>
      </Sezione>
      <div className="space-y-1.5">
        <label className="flex items-start gap-2.5 text-sm text-gray-900 leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 accent-[#013220]"
            checked={whatsapp}
            onChange={(e) => setWhatsapp(e.target.checked)}
            disabled={invio}
          />
          <span>{riempi(CONSENSO.whatsappB, v)}</span>
        </label>
        <p className="text-xs text-gray-400 leading-relaxed pl-[26px]">{riempi(CONSENSO.whatsappBNota, v)}</p>
      </div>
      <Azioni
        primaria={{ label: invio ? CONSENSO.invio : CONSENSO.acconsento, onClick: () => rispondi('acconsento'), disabled: invio }}
        secondaria={{ label: CONSENSO.nonAcconsento, onClick: () => rispondi('non_acconsento'), disabled: invio }}
      />
      <div className="space-y-2">
        {d.scade_il && <Nota>{riempi(CONSENSO.footer, v)}</Nota>}
        <Contatti telefono={d.mittente.telefono} email={d.mittente.email} riga />
      </div>
    </Corpo>,
  )
}
